import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

export type UserType = "agency" | "monitoring" | "admin";
export type OrgRole = "member" | "head";

export interface Organization {
  _id: string;
  name: string;
  org_type: "agency" | "monitoring_company";
  is_active: boolean;
}

export interface Department {
  _id: string;
  title: string;
}

export interface ActiveAssignment {
  _id: string;
  application_type: "universal" | "cgs";
  assigned_at: string;
}

export interface AuthUser {
  _id: string;
  name: string;
  email: string;
  user_type: UserType;
  org_role: OrgRole;
  organization: Organization | null;
  department: Department | null;
  permissions: string[];
  activeAssignments: ActiveAssignment[];
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get<AuthUser>("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount: check session via cookie
  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Listen for forced logout from the axios interceptor (refresh failed)
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener("auth:logout", handler);
    return () => window.removeEventListener("auth:logout", handler);
  }, []);

  const login = useCallback(async () => {
    setLoading(true);
    await fetchMe();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await axios.post("/auth/signout");
    } catch {
      // ignore — clear local state regardless
    }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
